<template>
    <div
        class="tab-pane active dashboard"
        :style="
            showWidgetConfigurator || (widgets.length === 0 && expandedWidget === null)
                ? 'overflow: hidden'
                : ''
        "
    >
        <div v-if="widgets.length === 0 && !isLoading && !expandedWidget" class="data-empty">
            <h3>{{ $t('dashboard.noWidgets') }}</h3>
        </div>
        <div ref="widgetsWrapper" class="widgets-wrapper">
            <div
                ref="widgetsWrapperChild"
                :class="['widgets-wrapper-child', `column-count-${widgetColumnCount}`]"
            >
                <div v-if="expandedWidget !== null" class="widget-wrapper expanded no-print">
                    <Component
                        :is="getComponentByType(expandedWidget.type)"
                        :id="expandedWidget.id"
                        :is-expanded="true"
                        :is-loading="loadQueue.includes(expandedWidget)"
                        :configuration="expandedWidget.configuration"
                        :content="content[expandedWidget.id]"
                        :class="{ 'is-expandable': widgets.length !== 0 }"
                        :widget-type="expandedWidget.type"
                        :widget-last-updated="expandedWidget.lastUpdated"
                        @expand="expandWidget"
                        @collapse="collapseWidget"
                        @delete="deleteWidget"
                        @edit="openEditWidgetConfigurator"
                    />
                </div>
                <div v-if="expandedWidget !== null" class="widget-wrapper expanded print-only">
                    <Component
                        :is="getComponentByType(expandedWidget.type)"
                        :id="expandedWidget.id"
                        :is-expanded="false"
                        :is-printver="true"
                        :is-loading="loadQueue.includes(expandedWidget)"
                        :configuration="expandedWidget.configuration"
                        :content="content[expandedWidget.id]"
                        :class="{ 'is-expandable': widgets.length !== 0 }"
                        :widget-type="expandedWidget.type"
                        :widget-last-updated="expandedWidget.lastUpdated"
                    />
                </div>
                <Grid
                    v-if="widgets.length > 0"
                    :animatable="gridAnimatable"
                    :draggable="widgets.length > 1"
                    :sortable="true"
                    :columns="widgetColumnCount"
                    :items="widgets"
                    :cell-height="WIDGET_HEIGHT"
                    :ref-scroll-element="scrollElement"
                    :wrapper-styles="{ overflow: 'visible' }"
                    @alltransitionend="onWidgetTransitionEnd"
                    @dragend="changeWidgetOrder"
                >
                    <template slot="cell" slot-scope="{ item: widget }">
                        <div class="widget-wrapper no-print">
                            <Component
                                :is="getComponentByType(widget.type)"
                                :id="widget.id"
                                :is-expanded="false"
                                :is-loading="loadQueue.includes(widget)"
                                :configuration="widget.configuration"
                                :content="content[widget.id]"
                                :class="{ 'is-expandable': widgets.length > 1 || expandedWidget !== null }"
                                :widget-type="widget.type"
                                :widget-last-updated="widget.lastUpdated"
                                @expand="expandWidget"
                                @delete="deleteWidget"
                                @edit="openEditWidgetConfigurator"
                            />
                        </div>
                        <div class="widget-wrapper print-only">
                            <Component
                                :is="getComponentByType(widget.type)"
                                :id="widget.id"
                                :is-expanded="false"
                                :is-printver="true"
                                :is-loading="loadQueue.includes(widget)"
                                :configuration="widget.configuration"
                                :content="content[widget.id]"
                                :class="{ 'is-expandable': widgets.length > 1 || expandedWidget !== null }"
                                :widget-type="widget.type"
                                :widget-last-updated="widget.lastUpdated"
                            />
                        </div>
                    </template>
                </Grid>
            </div>
        </div>
        <WidgetConfigurator
            v-if="showWidgetConfigurator"
            v-model="widgetConfiguration"
            :type.sync="widgetConfigurationType"
            :is-assignments-widget-exist="assignmentsWidgetAlreadyExist"
            @done="processWidgetConfiguration"
            @cancel="showWidgetConfigurator = false"
        />
    </div>
</template>
<script>
import WidgetConfigurator from './dashboard/WidgetConfigurator.vue';
import StubWidget from './dashboard/widgets/StubWidget.vue';
import SystemActivityWidget from './dashboard/widgets/SystemActivityWidget.vue';
import StaffPerformanceWidget from './dashboard/widgets/StaffPerformanceWidget.vue';
import TargetTimeWidget from './dashboard/widgets/TargetTimeWidget.vue';
import NursePresenceWidget from './dashboard/widgets/NursePresenceWidget.vue';
import RecentEventsWidget from './dashboard/widgets/RecentEventsWidget.vue';
import ExceptionTimeWidget from './dashboard/widgets/ExceptionTimeWidget.vue';
import AverageTimeWidget from './dashboard/widgets/AverageTimeWidget.vue';
import AssignmentsWidget from './dashboard/widgets/AssignmentsWidget.vue';
import LoaderWidget from './dashboard/widgets/LoaderWidget.vue';
import mappingState from './common/mappingState';
import {
    resourceWidgets,
    resourceWidgetData,
    resourceWidgetOrder,
    resourceAssignmentAreas,
} from '../api/resources';
import { mapState, mapMutations, mapActions } from 'vuex';
import { getCancelToken } from '../api';
import { prepareWidgetConfiguration } from './dashboard/widgetHelpers';
import { transformWidgetConfiguration } from './dashboard/widgetChartHelpers';
import orderBy from 'lodash-es/orderBy';
import find from 'lodash-es/find';
import filter from 'lodash-es/filter';
import maxBy from 'lodash-es/maxBy';
import debounce from 'lodash-es/debounce';
import flatten from 'lodash-es/flatten';
import map from 'lodash-es/map';
import { getTimeString, formatDateString, ifIsNotCanceled, getDateFormat } from './common/helpers';
import {
    AUTOREFRESH_INTERVAL,
    DEBOUNCE_INTERVAL,
    ERROR,
    SUCCESS,
    WIDGET_SETTINGS,
    WIDGET_TYPES,
    ROUTE_NAMES,
    USER_TYPES,
} from '../constants';

export default {
    components: {
        WidgetConfigurator,
        StubWidget,
        SystemActivityWidget,
        StaffPerformanceWidget,
        TargetTimeWidget,
        NursePresenceWidget,
        RecentEventsWidget,
        ExceptionTimeWidget,
        AverageTimeWidget,
        AssignmentsWidget,
        LoaderWidget,
    },

    mixins: [mappingState],

    constants: {
        WIDGET_HEIGHT: WIDGET_SETTINGS.HEIGHT,
    },

    data() {
        return {
            addingAssignmentsWidget: false,
            gridAnimatable: false,
            widgets: [],
            expandedWidget: null,
            loadQueue: [],
            content: {},
            initialLoad: true,
            cancelToken: getCancelToken(),
            widgetCancelToken: null,
            showWidgetConfigurator: false,
            widgetConfiguration: {},
            widgetConfigurationType: 0,
            widgetConfigurationId: null,
            autoRefreshIntervalId: null,
            autoRefreshWidgetId: null,
            scrollElement: null,
            widgetColumnCount: 2,
            // this function must be marked for removing in handler listener before destroy component
            debounceResizeWidgets: debounce(() => this.resizeWidgets(), DEBOUNCE_INTERVAL),
        };
    },

    computed: {
        ...mapState({
            isLoading: 'isLoading',
            expandedWidgetIdFromStore: 'expandedWidgetId',
            allAssignmentAreas: 'assignmentArea',
            callTypes: 'callTypes',
            navCollapsed: state => state.navCollapsed,
        }),

        /**
         * Checks loaded widgets list for assignments widget existence
         * @returns {Boolean} exist or not exist
         */
        assignmentsWidgetAlreadyExist() {
            return (
                !!this.getAllWidgets().find(w => w.type === WIDGET_TYPES.Assignments) ||
                this.addingAssignmentsWidget
            );
        },

        isSuperUser() {
            // TODO this function should be global (in modules/user.js)
            return this.userType === USER_TYPES.SUPER_USER;
        },
    },

    watch: {
        navCollapsed() {
            this.resizeWidgets();
        },

        loadQueue(val) {
            if (this.initialLoad && !this.isLoading && val.length === 0) {
                this.initialLoad = false;
                if (this.expandedWidget !== null) {
                    this.runAutoRefresh(this.expandedWidget);
                }
            }
        },

        expandedWidget(widget) {
            if (!this.initialLoad && widget !== null) {
                this.runAutoRefresh(widget);
            }
        },

        widgets() {
            if (this.expandedWidget === null) {
                if (this.widgets.length === 1) {
                    this.expandWidget(this.widgets[0].id);
                }
            } else if (this.expandedWidgetIdFromStore === null && this.widgets.length > 0) {
                this.collapseWidget();
            }
        },
    },

    mounted() {
        this.scrollElement = this.$refs.widgetsWrapper;
    },

    created() {
        this.$nextTick(() => {
            window.addEventListener('resize', this.debounceResizeWidgets);
        });
        this.updateData(() => {
            this.setLoaded(true);
            return this.loadData();
        });
        this.loadData();
    },

    beforeDestroy() {
        if (this.autoRefreshIntervalId) {
            clearInterval(this.autoRefreshIntervalId);
        }

        window.removeEventListener('resize', this.debounceResizeWidgets);
        this.cancelToken.cancel();
        this.clearQueue();
    },

    methods: {
        ...mapMutations({
            showLoading: 'SHOW_LOADING',
            setExpandedWidgetId: 'SET_WIDGET_ID',
            updateData: 'SET_UPDATE_DATA',
            setLoaded: 'SET_LOADED',
        }),
        ...mapActions({
            popup: 'popup/popup',
            confirmation: 'confirm/confirmation',
            configButton: 'header/configButton',
        }),

        loadData() {
            this.clearQueue();
            this.widgets = [];
            // TODO: update expanded widget behaviour for super user after update of requests for dashboard
            this.expandedWidget = null;
            this.runAutoRefresh(null);
            return this.loadWidgetsList()
                .then(() => {
                    this.configButton({
                        buttonName: 'addButton',
                        action: () => this.openNewWidgetConfigurator(),
                        disabled: () => this.getAllWidgets().length > 9,
                    });
                })
                .catch(ifIsNotCanceled());
        },

        /**
         * Sets and/or clears data auto refresh for widget
         * @param {widget|null} expanded widget (or null)
         */
        runAutoRefresh(widget) {
            let id = widget && widget.id;

            if (id === this.autoRefreshWidgetId && this.autoRefreshIntervalId) {
                return;
            }

            if (this.autoRefreshIntervalId) {
                clearInterval(this.autoRefreshIntervalId);
            }

            if (id !== null) {
                this.autoRefreshIntervalId = setInterval(() => this.addToQueue(widget), AUTOREFRESH_INTERVAL);
                this.autoRefreshWidgetId = id;
            } else {
                this.autoRefreshIntervalId = null;
                this.autoRefreshWidgetId = null;
            }
        },

        /**
         * Shows success popup
         */
        showSuccessPopup() {
            this.popup({
                type: SUCCESS,
                header: this.$t('common.success'),
                text: this.$t('common.saved'),
            });
        },

        /**
         * Shows popup with given error
         * @param {string} error
         */
        showErrorPopup(error) {
            if (error) {
                this.popup({
                    type: ERROR,
                    header: this.$t('common.error'),
                    text: error,
                });
            }
        },

        /**
         * Loads list of widgets and starts widget content load process
         * @returns {Promise}
         */
        loadWidgetsList() {
            this.showLoading(true);

            return resourceWidgets({ cancelToken: this.cancelToken.token })
                .then(response => {
                    if (Array.isArray(response.data)) {
                        let widgets = response.data;

                        if (this.isSuperUser) {
                            widgets = widgets.filter(widget => {
                                // TODO is this the best way to check AA of widget?
                                // Maybe better to add assignmentAreaId into widgets table?
                                return widget.configuration.aa[0] === this.allAssignmentAreas[0].name;
                            });
                        }

                        widgets = widgets.map(widget => ({
                            ...widget,
                            lastUpdated: null,
                        }));
                        if (this.expandedWidgetIdFromStore !== null) {
                            this.expandedWidget = widgets.find(
                                item => item.id === this.expandedWidgetIdFromStore
                            );
                            this.setWidgets(filter(widgets, w => w.id !== this.expandedWidgetIdFromStore));
                        } else {
                            this.setWidgets(widgets);
                        }
                        this.addToQueue(...widgets);
                    } else {
                        this.setWidgets([]);
                    }
                })
                .then(() => {
                    this.showLoading(false);
                    this.resizeWidgets();

                    this.$nextTick(() => {
                        this.gridAnimatable = true;
                    });
                })
                .catch(ifIsNotCanceled({ reject: true }));
        },

        /**
         * Stores new widget to backend and adds it to load queue
         * @param {number} type
         * @param {Object} configuration
         */
        storeNewWidget(type, configuration) {
            if (type === WIDGET_TYPES.Assignments) {
                this.addingAssignmentsWidget = true;
            }
            let widgetOrder =
                this.getAllWidgets().length !== 0
                    ? maxBy(this.getAllWidgets(), 'widgetOrder').widgetOrder + 1
                    : 1;
            let loaderId = this.getAllWidgets().length !== 0 ? maxBy(this.getAllWidgets(), 'id').id + 1 : 1;
            let loader = { id: loaderId, type: WIDGET_TYPES.Loader, widgetOrder };
            this.setWidgets(this.widgets.concat(loader));
            return resourceWidgets({ method: 'post', data: { type, configuration } })
                .then(response => {
                    if (this.$router.currentRoute.name === ROUTE_NAMES.DASHBOARD) {
                        this.showSuccessPopup();
                        let widgetId = response.data.widget_id;
                        widgetOrder = this.getAllWidgets().find(item => item.id === loaderId).widgetOrder;
                        let newWidget = {
                            id: widgetId,
                            type,
                            configuration,
                            widgetOrder: widgetOrder,
                        };
                        this.addingAssignmentsWidget = false;
                        this.setWidgets(this.widgets.filter(w => w.id !== loaderId));
                        if (this.expandedWidget !== null && this.expandedWidget.id === loaderId) {
                            this.expandedWidget = null;
                        }
                        this.setWidgets(this.widgets.concat(newWidget));
                        this.addToQueue(newWidget);
                        this.resizeWidgets();
                    }
                })
                .catch(({ statusText }) => {
                    this.addingAssignmentsWidget = false;
                    this.showErrorPopup(statusText);
                    this.loadData();
                });
        },

        /**
         * Sends update request for existing widget
         * @param {number} id
         * @param {Object} configuration
         */
        updateExistingWidget(id, configuration) {
            return resourceWidgets({ method: 'post', id, data: configuration })
                .then(() => {
                    if (this.$router.currentRoute.name === ROUTE_NAMES.DASHBOARD) {
                        this.showSuccessPopup();
                        let widget = this.getAllWidgets().find(widget => widget.id === id);
                        widget.configuration = configuration;
                        this.removeFromQueue(id);
                        this.addToQueue(widget);
                    }
                })
                .catch(err => {
                    if (err.status === 404) {
                        this.loadData();
                    }
                    this.showErrorPopup(err.statusText);
                });
        },

        /**
         * Adds new widget or updates existing one after widget configuration change
         * @returns {Promise}
         */
        processWidgetConfiguration() {
            this.showWidgetConfigurator = false;

            let id = this.widgetConfigurationId;
            let configuration = prepareWidgetConfiguration(
                this.widgetConfiguration,
                this.allAssignmentAreas,
                this.callTypes
            );
            let type = this.widgetConfigurationType;

            if (this.userType === USER_TYPES.SUPER_USER) {
                resourceAssignmentAreas()
                    .then(response => {
                        if (response.data) {
                            if (response.data[0].id !== this.userAreaId) {
                                this.showLoading(false);
                                this.showErrorPopup(this.$t('error.common.forbidden'));
                                return Promise.reject(response);
                            }
                            return !id
                                ? this.storeNewWidget(type, configuration)
                                : this.updateExistingWidget(id, configuration);
                        }
                    })
                    .catch(ifIsNotCanceled());
            } else {
                return !id
                    ? this.storeNewWidget(type, configuration)
                    : this.updateExistingWidget(id, configuration);
            }
        },

        /**
         * Opens widget configurator modal window for adding new widget
         */
        openNewWidgetConfigurator() {
            this.widgetConfigurationId = null;
            this.widgetConfigurationType = 0;
            this.widgetConfiguration = { name: '' };
            this.showWidgetConfigurator = true;
        },

        /**
         * Opens widget configurator modal window for widget reconfiguration
         * @param {number} id Widget ID
         */
        openEditWidgetConfigurator(id) {
            let widget = find(this.getAllWidgets(), { id });
            if (!widget) {
                return;
            }

            this.widgetConfigurationId = widget.id;
            this.widgetConfigurationType = widget.type;
            this.widgetConfiguration = transformWidgetConfiguration(
                this.allAssignmentAreas,
                this.callTypes,
                widget.configuration
            );
            this.filterConfigurationLocations();
            this.showWidgetConfigurator = true;
        },

        /**
         * Returns component name for given widget type
         * @param {number} type widget type (1 - 8)
         * @returns {string}
         */
        getComponentByType(type) {
            let widgetNames = Object.keys(WIDGET_TYPES);
            let widgets = Object.values(WIDGET_TYPES);
            if (widgets.includes(type)) {
                let widgetName = widgetNames[widgets.indexOf(type)];
                return `${widgetName}Widget`;
            }

            return 'StubWidget';
        },

        /**
         * Expands widget and sets it to the first position on the page then scrolls to the top
         * @param {number} id
         * @param {bool} saveToStore
         */
        expandWidget(id, saveToStore = false) {
            if (this.expandedWidget !== null) {
                this.collapseWidget();
            }

            if (saveToStore) {
                this.setExpandedWidgetId(id);
            }
            this.expandedWidget = find(this.widgets, { id });
            this.setWidgets(filter(this.widgets, w => w.id !== id));

            this.$nextTick(() => {
                this.scrollElement.scrollTop = 0;
            });
        },

        /**
         * Collapses widget
         */
        collapseWidget() {
            this.setExpandedWidgetId(null);
            this.runAutoRefresh(null);

            if (this.expandedWidget !== null) {
                this.setWidgets(this.widgets.concat(this.expandedWidget));
                this.expandedWidget = null;
            }
        },

        /**
         * Removes widget from dashboard
         * @param {number} id
         */
        deleteWidgetById(id) {
            return (
                resourceWidgets({ method: 'delete', id })
                    .then(() => {
                        if (this.$router.currentRoute.name === ROUTE_NAMES.DASHBOARD) {
                            this.removeFromQueue(id);
                            // if was deleted expandedWidget collapse it
                            if (this.expandedWidget !== null && id === this.expandedWidget.id) {
                                this.collapseWidget();
                            }
                            this.setWidgets(this.widgets.filter(w => w.id !== id));
                        } else {
                            return Promise.reject('canceled');
                        }
                    })
                    .then(() => {
                        this.showLoading(false);
                        this.resizeWidgets();
                    })
                    // TODO Could fail/work incorrectly since Axios has different errors (https://jira.ascom-ws.com/browse/MERA-1547)
                    .catch(ifIsNotCanceled())
            );
        },

        deleteWidget(id) {
            let deletedWidget = this.widgets.find(item => item.id === id) || this.expandedWidget;
            return this.confirmation({
                confirmTitle: this.$t('dashboard.widgets.removingWidgetTitle'),
                confirmBody: this.$t('dashboard.widgets.removingWidget', {
                    name:
                        deletedWidget && deletedWidget.configuration ? deletedWidget.configuration.name : '',
                }),
            }).then(r => {
                if (!r) {
                    return;
                }
                this.showLoading(true);
                if (this.userType === USER_TYPES.SUPER_USER) {
                    resourceAssignmentAreas()
                        .then(response => {
                            if (response.data) {
                                if (response.data[0].id !== this.userAreaId) {
                                    this.showLoading(false);
                                    this.showErrorPopup(this.$t('error.common.forbidden'));
                                    return Promise.reject(response);
                                }
                                return this.deleteWidgetById(id);
                            }
                        })
                        .catch(ifIsNotCanceled());
                } else {
                    return this.deleteWidgetById(id);
                }
            });
        },

        /**
         * Adds widget(s) to widget data loading queue
         * @param {Widget} widgets
         */
        addToQueue(...widgets) {
            this.loadQueue = this.loadQueue.concat(widgets);
            this.processQueue();
        },

        /**
         * Processes one element from widget data loading queue
         */
        processQueue() {
            if (this.loadQueue.length === 0 || this.widgetCancelToken) {
                return;
            }

            let widget = this.loadQueue[0];
            let id = widget.id;
            this.widgetCancelToken = getCancelToken();

            let resources = [];

            // For assignments widget get active shift
            if (widget.type === 8) {
                let activeShift = this.getActiveShift();
                if (activeShift) {
                    resources.push(this.fetchWidgetData(id, this.widgetCancelToken.token));
                    resources.push(activeShift);
                }
            } else {
                resources.push(this.fetchWidgetData(id, this.widgetCancelToken.token));
            }
            return Promise.all(resources)
                .then(allData => {
                    let data = allData[0];
                    let shiftData = allData[1];
                    if (shiftData && data && !data.duty_has_selector) {
                        data.activeShift = shiftData.activeShift;
                        data.duty_has_selector = shiftData.overridden;
                    }
                    data =
                        data && widget.type === WIDGET_TYPES.RecentEvents
                            ? data.map(item => {
                                  let i = item;
                                  if (item.start_time) {
                                      i.st_date = formatDateString(item.start_time, getDateFormat());
                                      i.st_time = getTimeString(item.start_time);
                                  }
                                  return i;
                              })
                            : data;
                    this.$set(this.content, id, data);
                    widget.lastUpdated = new Date();
                    if (this.loadQueue[0].id === id) {
                        this.loadQueue.shift();
                    }
                    this.widgetCancelToken = null;

                    return this.processQueue();
                })
                .catch(ifIsNotCanceled({ hideLoader: false }));
        },

        /**
         * Fetches data for widget, sets it to null in the case of errors
         * @param {number} id
         * @param {CancelToken} cancelToken
         */
        fetchWidgetData(id, cancelToken) {
            return resourceWidgetData({ id, cancelToken })
                .then(response => response.data.data)
                .catch(
                    ifIsNotCanceled({
                        callback: error => {
                            if (error.status === 404) {
                                // remove non-existing widget from dashboard
                                this.setWidgets(this.widgets.filter(w => w.id !== id));
                            }
                        },
                    })
                );
        },

        getActiveShift() {
            if (!this.$store.state.dutySelector.duties) {
                return null;
            }
            let shiftData = {
                activeShift: this.$store.state.dutySelector.duties
                    .find(active => (active.override !== null ? active.override : active.default))
                    .state.toString(),
                overridden: this.$store.state.dutySelector.overridden,
            };
            return shiftData;
        },
        /**
         * Removes widget with id from widget data loading queue
         * @param {number} id
         */
        removeFromQueue(id) {
            let idx = this.loadQueue.findIndex(widget => widget.id === id);

            if (idx !== -1) {
                this.loadQueue.splice(idx, 1);
                if (idx === 0 && this.widgetCancelToken !== null) {
                    this.widgetCancelToken.cancel();
                    this.widgetCancelToken = null;
                }
            }
        },

        /**
         * Clears widget data loading queue
         */
        clearQueue() {
            if (this.loadQueue.length > 0) {
                this.loadQueue.splice(0, this.loadQueue.length);
                if (this.widgetCancelToken !== null) {
                    this.widgetCancelToken.cancel();
                    this.widgetCancelToken = null;
                }
            }
        },

        /**
         * Calculates the widgets' size
         */
        resizeWidgets() {
            if (this.$refs.hasOwnProperty('widgetsWrapper')) {
                let widgetsWrapperWidth = this.$refs.widgetsWrapper.clientWidth;
                let { COLUMN_BREAKPOINTS } = WIDGET_SETTINGS;

                for (let i = 0; i < COLUMN_BREAKPOINTS.length; i++) {
                    if (widgetsWrapperWidth < COLUMN_BREAKPOINTS[i]) {
                        this.widgetColumnCount = i + 1;
                        return;
                    }
                }

                this.widgetColumnCount = COLUMN_BREAKPOINTS.length + 1;
            }
        },

        /**
         * Changes widget order
         * @param {object} event
         */
        changeWidgetOrder(event) {
            let orderWasChanged = false;

            let newWidgetsOrder = event.items.map(({ item, sort }) => {
                let widgetOrder = sort;

                if (this.expandedWidget !== null) {
                    if (this.expandedWidget.widgetOrder <= widgetOrder) {
                        widgetOrder += 1;
                    }
                }

                let widget = find(this.widgets, { id: item.id });
                if (widget.widgetOrder !== widgetOrder) {
                    widget.widgetOrder = widgetOrder;
                    orderWasChanged = true;
                }

                return {
                    widgetId: item.id,
                    widgetOrder,
                };
            });

            if (orderWasChanged) {
                resourceWidgetOrder({
                    method: 'post',
                    data: newWidgetsOrder,
                })
                    .then(() => {
                        // do nothing
                    })
                    .catch(() => {
                        // do nothing
                    });
            }
        },

        onWidgetTransitionEnd() {
            // this is doing for print version
            this.setWidgets(this.widgets);
        },

        /**
         * sets ordinary widgets
         * @param {array} widgets
         */
        setWidgets(widgets) {
            this.widgets = orderBy(widgets, 'widgetOrder');
        },

        /**
         * returns array of the expanded widget and ordinary widgets
         * @returns {array}
         */
        getAllWidgets() {
            return this.widgets.concat(this.expandedWidget || []);
        },

        /**
         * removes inexistent locations from widgetConfiguration,
         * that have been renamed or removed in NISMGui
         * and that are part of not selected AA
         */
        filterConfigurationLocations() {
            let allLocationsNames = this.allAssignmentAreas
                .reduce((result, area) => result.concat(area.locations), [])
                .map(location => location.name);

            let selectedAA = this.allAssignmentAreas.filter(aa =>
                this.widgetConfiguration.aa.includes(aa.name)
            );
            let locationsInSelectedAA = map(flatten(map(selectedAA, aa => aa.locations)), 'name');

            if (this.widgetConfiguration.locations) {
                this.widgetConfiguration.locations = [
                    ...this.widgetConfiguration.locations.filter(
                        location =>
                            allLocationsNames.includes(location) && locationsInSelectedAA.includes(location)
                    ),
                ];
            }
        },
    },
};
</script>
